import { useState, useEffect, useRef, ReactNode, ChangeEvent, useMemo } from 'react';
import useSWR from 'swr';
import { useLocation } from 'wouter';
import { useAuthStore } from '../../hooks/useAuth';
import { isOperationsTierUser } from '../entitlements/OperationsFeatureGate';
import { useUIStore } from '../../hooks/useUIStore';
import { useDetentionLogic } from '../../hooks/useDetentionLogic';
import toast from 'react-hot-toast';
import { showCenteredCheckoutError } from '../../lib/toastHelpers';
import { formatDateInTimeZone } from '../../utils/timezoneDisplay';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogPortal,
  DialogOverlay,
  DialogClose,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../ui/alert-dialog';
import {
  Loader2,
  Truck,
  FileText,
  MessageSquare,
  StickyNote,
  Paperclip,
  Clock,
  AlertTriangle,
  X,
  CheckCircle,
  Flag,
  ChevronDown,
  UserCheck,
  XCircle,
  LogOut,
  RotateCcw,
  ArrowRightCircle,
  Package,
  ClipboardCheck,
  ExternalLink,
  MapPin,
} from 'lucide-react';
import { CreateFollowUpDialog } from './CreateFollowUpDialog';
import { LateTimeModal } from '../LateTimeModal';
import { formatPOMetadata, hasPOMetadata } from '../../utils/poMetadataHelpers';
import { summarizeCompatibilityStatus } from '../../utils/dockCompatibilityHelpers';
import { formatDockCapabilities } from '../../utils/formatDockCapabilities';
import { formatPOException, EXCEPTION_OPTIONS, EXCEPTION_EXPLANATIONS, type POExceptionType } from '../../utils/formatPOException';
import { extractPOExceptionFromReceiptNotes, stripExceptionFromNotes } from '../../utils/extractPOExceptionFromReceiptNotes';
import { POReceiptOutcomeBadge } from './POReceiptOutcomeBadge';
import { AppointmentTimelineSection } from './AppointmentTimelineSection';
import { DocumentsTabContent } from './DocumentsTabContent';
import { MessagesTabContent } from './MessagesTabContent';

// Helper types
interface Note {
  id: string;
  content: string;
  createdAt: string;
}

const fetcher = async ([url, token]: [string, string]) => {
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('Session expired');
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || 'Failed to fetch');
  }
  return res.json();
};

/** Derive direction from linked POs */
function deriveDirection(linkedPOs?: any[]): 'inbound' | 'outbound' | 'mixed' | null {
  if (!linkedPOs || linkedPOs.length === 0) return null;
  const types = new Set(linkedPOs.map((po: any) => po.type).filter(Boolean));
  if (types.size === 0) return null;
  if (types.size === 1) return types.has('inbound') ? 'inbound' : 'outbound';
  return 'mixed';
}

/** Comprehensive status color mapping */
function getStatusColor(status: string): string {
  switch (status) {
    case 'completed': return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400';
    case 'cancelled': case 'declined': return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
    case 'in_progress': return 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400';
    case 'checked_in': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400';
    case 'approved': case 'confirmed': case 'scheduled': return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400';
    case 'pending': case 'requested': return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400';
    default: return 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300';
  }
}




export function AppointmentDetailDialog() {
  const { token, user } = useAuthStore();
  const {
    isAppointmentDetailOpen,
    selectedAppointment,
    closeAppointmentDetail,
    appointmentDetailScrollToRisk,
    setAppointmentDetailScrollToRisk,
    notifyActionSuccess,
    appointmentDetailInitialTab,
  } = useUIStore();

  // Internal state
  const [activeTab, setActiveTab] = useState<'overview' | 'documents' | 'messages' | 'notes'>(appointmentDetailInitialTab);

  const [newMessage, setNewMessage] = useState('');
  const [newNote, setNewNote] = useState('');
  const [uploading, setUploading] = useState(false);

  // Action guards
  const [isSending, setIsSending] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Outcome & Follow-up State
  const [isFollowUpOpen, setIsFollowUpOpen] = useState(false);
  const [isOutcomeEditing, setIsOutcomeEditing] = useState(false);
  const [outcomeType, setOutcomeType] = useState('confirmed_issue');
  const [outcomeNotes, setOutcomeNotes] = useState('');
  const [outcomeSubmitting, setOutcomeSubmitting] = useState(false);

  const [metadataExpanded, setMetadataExpanded] = useState(false);

  // Action states
  const [actionLoading, setActionLoading] = useState<string | null>(null); // 'approve'|'reject'|'checkin'|'checkout'
  const [lateCheckoutPending, setLateCheckoutPending] = useState(false);
  const [lateCheckinPending, setLateCheckinPending] = useState(false);
  const [reopeningPoId, setReopeningPoId] = useState<string | null>(null);

  // Dock reassignment state
  const [showDockReassign, setShowDockReassign] = useState(false);
  const [reassignDockId, setReassignDockId] = useState('');
  const [reassignLoading, setReassignLoading] = useState(false);

  // Revise time state
  const [showReviseTime, setShowReviseTime] = useState(false);
  const [reviseDate, setReviseDate] = useState('');
  const [reviseTime, setReviseTime] = useState('09:00');
  const [reviseNote, setReviseNote] = useState('');
  const [reviseLoading, setReviseLoading] = useState(false);

  // Accidental Completion Correction state
  const [showCorrectionDialog, setShowCorrectionDialog] = useState(false);
  const [correctionReason, setCorrectionReason] = useState('');
  const [correctionLoading, setCorrectionLoading] = useState(false);
  const [correctionWarnings, setCorrectionWarnings] = useState<any[]>([]);

  // Confirmation dialog state
  const [pendingConfirm, setPendingConfirm] = useState<{
    action: string;
    title: string;
    description: string;
    confirmLabel: string;
    confirmClass: string;
    body?: Record<string, string>;
    content?: ReactNode;
  } | null>(null);

  // Phase 2: Receipt reconciliation state
  const [receiptEditing, setReceiptEditing] = useState(false);
  const [receiptDraft, setReceiptDraft] = useState<Record<string, { received: number; status: string; notes: string; exception: string }>>({}); 
  const [receiptSaving, setReceiptSaving] = useState(false);

  // Phase 3: Follow-up PO creation state
  const [followupCreating, setFollowupCreating] = useState<string | null>(null); // PO ID currently creating follow-up for
  const [followupPoMap, setFollowupPoMap] = useState<Record<string, { poNumber: string; poId: string }>>({}); // Tracks freshly-created follow-up PO refs

  // Shortage safeguard: deferred checkout data held while warning is shown
  const [shortageWarningPending, setShortageWarningPending] = useState<{
    confirmPayload: typeof pendingConfirm;
  } | null>(null);

  // Receipt gate: blocks completion when unloading results are missing
  const [receiptGateOpen, setReceiptGateOpen] = useState(false);



  const appointmentId = selectedAppointment?.id;

  // Data Fetching
  const { data, error, isLoading, mutate } = useSWR(
    isAppointmentDetailOpen && appointmentId && token ? [`/api/appointments/${appointmentId}/details`, token] : null,
    fetcher,
    {
      // Only poll when viewing messages
      refreshInterval: activeTab === 'messages' ? 10000 : 0,
      revalidateOnFocus: true,
    }
  );



  // Fetch warehouse docks for approval and reassignment picker (warehouse roles only)
  const isWarehouseUser = Boolean(
    user?.role && ['warehouse_manager', 'warehouse_staff', 'admin'].includes(user.role)
  );
  const warehouseId = user?.warehouseId || data?.appointment?.warehouseId;
  const { data: docksData } = useSWR(
    isAppointmentDetailOpen && token && isWarehouseUser && warehouseId ? [`/api/warehouses/${warehouseId}/docks`, token] : null,
    fetcher
  );
  const { data: appointmentsData } = useSWR(
    isAppointmentDetailOpen && token && isWarehouseUser ? ['/api/appointments', token] : null,
    fetcher
  );
  const availableDocks = (docksData?.docks || []).filter((d: any) => {
    if (d.isActive === false) return false;
    const s = (d.status || '').toLowerCase();
    return s !== 'maintenance' && s !== 'oos' && !s.includes('out of service') && !s.includes('out_of_service');
  }).sort((a: any, b: any) => String(a.dockNumber || '').localeCompare(String(b.dockNumber || ''), undefined, { numeric: true, sensitivity: 'base' }));
  const conflictingDockIds = useMemo(() => {
    const ids = new Set<string>();
    const appt = data?.appointment;
    if (!appt?.startAt || !appt?.endAt) return ids;
    const startMs = new Date(appt.startAt).getTime();
    const endMs = new Date(appt.endAt).getTime();
    if (Number.isNaN(startMs) || Number.isNaN(endMs)) return ids;
    const allAppts = Array.isArray(appointmentsData?.appointments) ? appointmentsData.appointments : [];
    for (const other of allAppts) {
      if (!other || other.id === appt.id) continue;
      const otherDockId = other.dockId || other.dock_id;
      if (!otherDockId) continue;
      if (!['approved', 'scheduled', 'checked_in', 'in_progress'].includes(other.status)) continue;
      const oStart = new Date(other.startAt).getTime();
      const oEnd = new Date(other.endAt).getTime();
      if (oStart < endMs && oEnd > startMs) {
        ids.add(otherDockId);
      }
    }
    return ids;
  }, [data?.appointment, appointmentsData?.appointments]);
  const nonConflictingDocks = useMemo(
    () => availableDocks.filter((d: any) => !conflictingDockIds.has(d.id)),
    [availableDocks, conflictingDockIds]
  );
  const preferredDefaultDockId = useMemo(() => {
    const existingDockId = data?.appointment?.dockId || data?.appointment?.dock_id;
    if (existingDockId && !conflictingDockIds.has(existingDockId)) {
      return existingDockId;
    }
    const completelyFree = nonConflictingDocks.find(
      (d: any) => (d.status || '').toLowerCase() !== 'occupied'
    );
    return completelyFree?.id || nonConflictingDocks[0]?.id || '';
  }, [data?.appointment?.dockId, data?.appointment?.dock_id, conflictingDockIds, nonConflictingDocks]);

  // Reset per-appointment / per-open UI state (prevents "carry-over")
  useEffect(() => {
    if (isAppointmentDetailOpen && appointmentId) {
      // Jump to the requested tab when the dialog opens (e.g. 'documents' from PO drawer)
      setActiveTab(appointmentDetailInitialTab);
    } else {
      // Close follow-up if parent closes OR if we switch appointments
      setIsFollowUpOpen(false);
      setIsOutcomeEditing(false);
      setShowDockReassign(false);
      setReassignDockId('');
      setShowReviseTime(false);
      setReviseDate('');
      setReviseTime('09:00');
      setReviseNote('');
      setActiveTab('overview');
    }
  }, [appointmentId, isAppointmentDetailOpen, appointmentDetailInitialTab]);

  // Handle deep link to risk panel (target removed; just clear the flag)
  useEffect(() => {
    if (isAppointmentDetailOpen && appointmentDetailScrollToRisk) {
      setAppointmentDetailScrollToRisk(false);
    }
  }, [isAppointmentDetailOpen, appointmentDetailScrollToRisk, setAppointmentDetailScrollToRisk]);



  // Mark messages read
  useEffect(() => {
    if (isAppointmentDetailOpen && activeTab === 'messages' && token && appointmentId) {
      fetch(`/api/appointments/${appointmentId}/messages/read`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(console.error); // Silent fail ok
    }
  }, [isAppointmentDetailOpen, activeTab, appointmentId, token]);

  // Format helpers
  const formatTime = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return formatDateInTimeZone(dateStr, data?.warehouseTimeZone, true);
  };

  // Actions
  const sendMessage = async () => {
    if (!newMessage.trim() || !token || !appointmentId || isSending) return;

    setIsSending(true);
    try {
      await fetch(`/api/appointments/${appointmentId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: newMessage }),
      });
      setNewMessage('');
      await mutate(); // Deterministic update
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !token || !appointmentId || isSaving) return;

    setIsSaving(true);
    try {
      await fetch(`/api/appointments/${appointmentId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: newNote }),
      });
      setNewNote('');
      await mutate();
      toast.success('Note added');
    } catch (err) {
      toast.error('Failed to add note');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>, fileType: string) => {
    const file = e.target.files?.[0];
    if (!file || !token || !appointmentId) return;

    setUploading(true);
    try {
      // Read file as base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });

      // Upload to backend
      const res = await fetch(`/api/appointments/${appointmentId}/documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          fileName: file.name,
          fileUrl: base64,
          fileType,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(data.error || `Upload failed (${res.status})`);
      }

      // Refresh document list, then show success
      await mutate();
      toast.success(`${fileType} uploaded`);
    } catch (err: any) {
      toast.error(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const saveOutcome = async () => {
    if (!token || !appointmentId || outcomeSubmitting) return;

    setOutcomeSubmitting(true);
    try {
      await fetch(`/api/appointments/${appointmentId}/outcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ outcome: outcomeType, notes: outcomeNotes }),
      });
      toast.success('Outcome saved');
      setIsOutcomeEditing(false);
      await mutate();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save outcome');
    } finally {
      setOutcomeSubmitting(false);
    }
  };

  // Detention Logic — uses warehouse-authoritative detention settings from the details endpoint
  const detentionAppt = useMemo(() => ({ scheduledTime: data?.appointment?.startAt ?? null, actualArrivalTime: data?.appointment?.checkInTime ?? null, checkOutTime: data?.appointment?.checkOutTime ?? null }), [data?.appointment?.startAt, data?.appointment?.checkInTime, data?.appointment?.checkOutTime]);
  const detentionData = useDetentionLogic(detentionAppt, data?.warehouseDetention ?? undefined);

  // Auto-close on route change (only when moving AWAY from current location)
  const [location, setLocation] = useLocation();
  const prevLocationRef = useRef(location);

  useEffect(() => {
    if (prevLocationRef.current !== location) {
      prevLocationRef.current = location;
      if (isAppointmentDetailOpen) {
        closeAppointmentDetail();
      }
    }
  }, [location, isAppointmentDetailOpen, closeAppointmentDetail]);



  /** Consolidates the 3 duplicate receiptDraft initialisation blocks.
   *  `mode='new'` → simple defaults for first-time receipt recording
   *  `mode='edit'` → parses existing exception tags & cleans notes */
  const startReceiptEditing = (mode: 'new' | 'edit' = 'new') => {
    if (!data?.linkedPOs || data.linkedPOs.length === 0) return;
    const initial: Record<string, { received: number; status: string; notes: string; exception: string }> = {};
    data.linkedPOs.forEach((po: any) => {
      if (mode === 'edit') {
        const existingStatus = po.receiptStatus ?? 'complete';
        const isLegacyException = existingStatus === 'damaged' || existingStatus === 'refused';
        const rawNotes = po.receiptNotes ?? '';
        const parsedException = extractPOExceptionFromReceiptNotes(rawNotes) ?? '';
        const cleanNotes = stripExceptionFromNotes(rawNotes);
        initial[po.id] = { received: po.receivedPallets ?? po.expectedPallets ?? 0, status: isLegacyException ? 'short' : existingStatus, notes: cleanNotes, exception: isLegacyException ? existingStatus : parsedException };
      } else {
        initial[po.id] = { received: po.receivedPallets ?? po.expectedPallets ?? 0, status: po.receiptStatus ?? 'complete', notes: '', exception: '' };
      }
    });
    setReceiptEditing(true);
    setReceiptDraft(initial);
  };

  // ----------------------------------------------------------------
  // Appointment action handlers (no new endpoints)
  // ----------------------------------------------------------------
  const runAction = async (action: string, body?: Record<string, string>) => {
    if (!token || !appointmentId || actionLoading) return;

    // Issue 1 fix: Approve without dock → hand off to Dashboard dock modal
    if (action === 'approve') {
      const appt = data?.appointment;
      const existingDockId = appt?.dockId || appt?.dock_id || '';
      const safeExistingDockId = existingDockId && !conflictingDockIds.has(existingDockId) ? existingDockId : '';
      const resolvedDockId =
        body?.dockId ||
        reassignDockId ||
        safeExistingDockId ||
        '';
      if (!resolvedDockId) {
        toast.error('Please select an available dock to approve this appointment.');
        return;
      }
      body = { ...(body || {}), dockId: resolvedDockId };
    }

    setActionLoading(action);
    try {
      let endpoint = '';
      let method = 'POST';
      if (action === 'approve') { endpoint = `/api/appointments/${appointmentId}/approve`; }
      else if (action === 'reject') { endpoint = `/api/appointments/${appointmentId}/decline`; method = 'POST'; }
      else if (action === 'checkin') {
        if (data?.po?.status === 'on_hold') {
          toast.error('Cannot check-in: Purchase Order is on hold.');
          setActionLoading(null);
          return;
        }
        // Yard arrival: PATCH /status → checked_in
        endpoint = `/api/appointments/${appointmentId}/checkin`;
        method = 'POST';
        body = {} as any;
      }
      else if (action === 'dispatch') {
        // Dispatch to dock: PATCH /status → in_progress
        endpoint = `/api/appointments/${appointmentId}/status`;
        method = 'PATCH';
        body = { status: 'in_progress' } as any;
      }
      else if (action === 'checkout') { endpoint = `/api/appointments/${appointmentId}/checkout`; }
      else if (action === 'uncheckin') { endpoint = `/api/appointments/${appointmentId}/uncheckin`; method = 'PATCH'; }
      else if (action === 'reassignDock') { endpoint = `/api/appointments/${appointmentId}/reassign-dock`; method = 'PATCH'; }
      else if (action === 'undispatch') { endpoint = `/api/appointments/${appointmentId}/undispatch`; method = 'PATCH'; }
      else if (action === 'unapprove') { endpoint = `/api/appointments/${appointmentId}/unapprove`; method = 'PATCH'; }
      else if (action === 'cancel') { endpoint = `/api/appointments/${appointmentId}/cancel`; }

      const res = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body || {}),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        // Late checkout — open time confirmation modal instead of surfacing error
        if (err.error === 'LATE_CHECKOUT_REQUIRES_TIME') {
          setLateCheckoutPending(true);
          return;
        }
        if (err.error === 'LATE_CHECKIN_REQUIRES_TIME') {
          setLateCheckinPending(true);
          return;
        }
        if (err.error === 'RECEIPTS_REQUIRED_BEFORE_COMPLETION') {
          setReceiptGateOpen(true);
          return;
        }
        throw new Error(err.error || err.message || `Action failed (${res.status})`);
      }

      const resJson = await res.json().catch(() => ({}));
      await mutate();
      notifyActionSuccess(); // Notify all listeners (Dashboard, Carriers, etc.)
      if (action === 'reject') {
        toast.success('Request declined');
      } else if (action === 'checkin') {
        toast.success(`Check-in successful${resJson.lateStatus ? ` (${resJson.lateStatus})` : ''}`);
      } else if (action === 'checkout') {
        toast.success('Appointment completed');
      } else {
        toast.success(action.charAt(0).toUpperCase() + action.slice(1) + ' successful');
      }
    } catch (err: any) {
      // Only log truly unexpected errors
      console.error(`[AppointmentModal] Unexpected error during ${action}:`, err);
        if (action === 'checkout') {
            showCenteredCheckoutError(err.message || `Failed to ${action}`);
        } else {
            toast.error(err.message || `Failed to ${action}`);
        }
    } finally {
      setActionLoading(null);
    }
  };

  // Revise time handler — mirrors RequestDetailModal revise flow
  const handleReviseTime = async () => {
    if (!reviseDate || !reviseTime) {
      toast.error('Date and time are required');
      return;
    }
    if (!token || !appointmentId) return;
    setReviseLoading(true);
    try {
      const res = await fetch(`/api/appointments/${appointmentId}/revise-time`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ requestedDate: reviseDate, requestedTime: reviseTime, note: reviseNote }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to revise time');
      }
      toast.success('Time revision sent — Carrier notified of new arrival time', { duration: 4000 });
      setShowReviseTime(false);
      setReviseDate('');
      setReviseTime('09:00');
      setReviseNote('');
      await mutate();
      notifyActionSuccess();
    } catch (err: any) {
      toast.error(err.message || 'Failed to revise time');
    } finally {
      setReviseLoading(false);
    }
  };

  if (!isAppointmentDetailOpen) return null;

  // Wrap in fragment to support AlertDialog outside the main Dialog
  return (<>
    <Dialog open={isAppointmentDetailOpen} onOpenChange={(open) => !open && closeAppointmentDetail()}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent className="
            sm:max-w-4xl max-w-full
            sm:max-h-[90vh] max-h-[95dvh]
            w-full flex flex-col p-0 gap-0 overflow-hidden
            bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-50
            border-slate-200 dark:border-slate-800
            dialog-sheet-mobile
          ">
          {/* Header */}
          <DialogHeader className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200 dark:border-slate-800 flex flex-row items-center justify-between space-y-0 bg-slate-50 dark:bg-slate-900/50">
            <div className="flex flex-col gap-1.5 min-w-0 mr-2">
              <div className="flex items-center gap-2.5 flex-wrap">
                <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white truncate max-w-[240px] sm:max-w-none">{(() => {
                  const loadNum = data?.loadRef?.loadNumber;
                  const primaryPo = data?.linkedPOs?.[0]?.poNumber;
                  const apptPo = data?.appointment?.customerPoNumber || selectedAppointment?.po_number;
                  const apptId = data?.appointment?.id || appointmentId;
                  if (loadNum) return `Load ${loadNum}${apptPo ? ` · ${apptPo}` : primaryPo ? ` · ${primaryPo}` : ''}`;
                  if (primaryPo) return `PO ${primaryPo}`;
                  if (apptPo) return `PO ${apptPo}`;
                  if (apptId) return `Appointment #${String(apptId).slice(0, 8)}`;
                  return 'Appointment Detail';
                })()}</DialogTitle>
                {/* Status Pill — authoritative status display */}
                {data?.appointment && (
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider shrink-0 ${getStatusColor(data.appointment.status)}`}>
                    {data.appointment.status === 'cancelled'
                      ? (data.appointment.notes || data.appointment.cancelReason || '').toLowerCase().includes('cancelled by carrier')
                        ? 'Cancelled by Carrier'
                        : (data.appointment.notes || data.appointment.cancelReason || '').toLowerCase().includes('declined')
                          ? 'Declined by Warehouse'
                          : 'Cancelled by Warehouse'
                      : data.appointment.status.replace(/_/g, ' ')}
                  </span>
                )}
                {data?.linkedPOs && data.linkedPOs.length > 1 && (
                  <span className="text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded-full border border-slate-200 dark:border-slate-700 shrink-0">
                    +{data.linkedPOs.length - 1} more
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400 font-normal flex-wrap mt-0.5">
                <span className="flex items-center gap-1.5">
                  <Truck className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 shrink-0" /> {data?.appointment?.carrierName || selectedAppointment?.carrier_name || 'Unassigned Carrier'}
                </span>
                {data?.appointment?.startAt && (
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 shrink-0" /> {formatTime(data.appointment.startAt)}
                  </span>
                )}
                {/* Dock in header */}
                {data?.appointment?.dockNumber && (
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 shrink-0" /> Dock {data.appointment.dockNumber}
                  </span>
                )}
                {/* Direction indicator */}
                {(() => {
                  const dir = deriveDirection(data?.linkedPOs);
                  if (!dir) return null;
                  const dirLabel = dir === 'inbound' ? '← Inbound' : dir === 'outbound' ? '→ Outbound' : '↔ Mixed';
                  return <span className="flex items-center gap-1.5"><span className="text-slate-400 dark:text-slate-500">•</span> {dirLabel}</span>;
                })()}
              </div>
            </div>

            {/* Explicit Close Button */}
            <DialogClose
              onClick={closeAppointmentDetail}
              className="rounded-full p-2 hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-500 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </DialogClose>
          </DialogHeader>

          {/* PO Hold Banner */}
          {data?.po?.status === 'on_hold' && (
            <div className="bg-red-50 dark:bg-red-950/30 border-y border-red-200 dark:border-red-800/60 px-6 py-3 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
              <div>
                <h4 className="text-sm font-semibold text-red-700 dark:text-red-300 flex items-center gap-2">
                  PO ON HOLD
                  {data.po.holdReason && typeof data.po.holdReason === 'string' && (
                    <span className="text-xs px-2 py-0.5 bg-red-600 text-white rounded-full uppercase tracking-wider font-bold">
                      {data.po.holdReason.replace(/_/g, ' ')}
                    </span>
                  )}
                </h4>
                <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                  {data.po.holdNotes ? data.po.holdNotes : 'This purchase order is currently on hold. Operations are restricted.'}
                </p>
                <p className="text-xs text-red-500 dark:text-red-400/80 mt-1 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  Placed on hold {formatTime(data.po.holdSetAt)}
                </p>
              </div>
            </div>
          )}


          {/* Stale Data Recovery Banner: PO closed but receipts missing on active appointment */}
          {data?.appointment && ['checked_in', 'in_progress'].includes(data.appointment.status) && data?.linkedPOs && (() => {
            const closedMissingReceipt = data.linkedPOs.filter((po: any) => po.status === 'closed' && !po.receiptStatus);
            if (closedMissingReceipt.length === 0) return null;
            return (
              <div className="bg-amber-500/10 border-y border-amber-500/20 px-6 py-3 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-amber-600 dark:text-amber-400">
                    Receiving Results Missing
                  </h4>
                  <p className="text-sm text-amber-600/80 dark:text-amber-400/80 mt-0.5">
                    {closedMissingReceipt.length === 1
                      ? `Linked PO ${closedMissingReceipt[0].poNumber} is completed, but receiving results are missing.`
                      : `${closedMissingReceipt.length} linked POs are completed but receiving results are missing.`
                    }
                    {' '}Reopen the PO to record receiving results, then complete this appointment.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {closedMissingReceipt.map((po: any) => (
                      <button
                        key={po.id}
                        disabled={reopeningPoId === po.id}
                        onClick={async () => {
                          if (!token) return;
                          setReopeningPoId(po.id);
                          try {
                            const res = await fetch(`/api/warehouse-pos/${po.id}/status`, {
                              method: 'PATCH',
                              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                              body: JSON.stringify({ status: 'open' }),
                            });
                            if (!res.ok) {
                              const d = await res.json().catch(() => ({}));
                              throw new Error(d.error || 'Failed to reopen PO');
                            }
                            toast.success(`PO ${po.poNumber} reopened`);
                            await mutate();
                            notifyActionSuccess();
                          } catch (err: any) {
                            toast.error(err.message || 'Failed to reopen PO');
                          } finally {
                            setReopeningPoId(null);
                          }
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-500/15 hover:bg-amber-200 dark:hover:bg-amber-500/25 border border-amber-300 dark:border-amber-500/30 rounded-md transition-colors disabled:opacity-50"
                      >
                        {reopeningPoId === po.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <RotateCcw className="w-3 h-3" />
                        )}
                        Reopen {po.poNumber}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Loading / Error States */}
          {isLoading ? (
            <div className="flex-1 flex flex-col items-center justify-center p-12">
              <Loader2 className="w-10 h-10 animate-spin text-blue-500 mb-4" />
              <p className="text-muted-foreground">Loading details...</p>
            </div>
          ) : error ? (
            <div className="flex-1 flex flex-col items-center justify-center p-12 text-red-500">
              <AlertTriangle className="w-10 h-10 mb-4" />
              <p>Failed to load appointment details.</p>
              <button onClick={() => mutate()} className="mt-4 text-sm underline hover:text-red-600">
                Retry
              </button>
            </div>
          ) : (
            <>
              {/* Tabs Navigation */}
              <div className="flex border-b border-slate-200 dark:border-slate-800 px-3 sm:px-6 bg-white dark:bg-slate-950 overflow-x-auto">
                {[
                  { id: 'overview', label: 'Overview', mobileLabel: null, icon: FileText },
                  { id: 'documents', label: `Documents (${data?.documents?.length || 0})`, mobileLabel: `Docs (${data?.documents?.length || 0})`, icon: Paperclip },
                  { id: 'messages', label: `Messages (${data?.messages?.length || 0})`, mobileLabel: `Msgs (${data?.messages?.length || 0})`, icon: MessageSquare },
                  {
                    id: 'notes',
                    label: `Notes (${data?.notes?.length || 0})`,
                    mobileLabel: null,
                    icon: StickyNote,
                    hidden: user?.role === 'carrier',
                  },
                ].map(
                  (tab) =>
                    !tab.hidden && (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id as any)}
                        className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold border-b-2 transition-colors shrink-0 ${activeTab === tab.id
                          ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400 font-bold'
                          : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 font-medium'
                          }`}
                      >
                        <tab.icon className="w-4 h-4 shrink-0 hidden sm:block" />
                        {tab.mobileLabel ? (
                          <>
                            <span className="sm:hidden">{tab.mobileLabel}</span>
                            <span className="hidden sm:inline">{tab.label}</span>
                          </>
                        ) : tab.label}
                      </button>
                    )
                )}
              </div>

              {/* Content Area */}
              <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-slate-50/50 dark:bg-slate-950/50">
                {/* OVERVIEW TAB */}
                {activeTab === 'overview' && data?.appointment && (
                  <div className="space-y-4">

                      {/* === (A) DOCK CONTEXT + NEXT STEP + ACTION BAR === */}
                      {/* No-dock warning or released dock historical notice */}
                      {!data.appointment.dockNumber && (
                        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-950/20 px-5 py-3">
                          <span className="flex items-center gap-1.5 text-sm text-amber-600 dark:text-amber-400 font-semibold">
                            <MapPin className="w-4 h-4" />
                            {data.appointment.status === 'cancelled' && (data.approvalHistory?.lastAssignedDockNumber || data.appointment.lastAssignedDockNumber)
                              ? `Dock released upon cancellation (previously assigned to Dock ${data.approvalHistory?.lastAssignedDockNumber || data.appointment.lastAssignedDockNumber})`
                              : 'No dock assigned'}
                          </span>
                        </div>
                      )}

                      {/* Action area — merged next-step + buttons */}
                      {user?.role !== 'carrier' && (() => {
                        const s = data.appointment.status;
                        const hasReceipt = data?.linkedPOs?.some((po: any) => po.receiptStatus);

                        // Next-step message
                        const stepMsg =
                          s === 'requested' || s === 'pending' ? 'Awaiting manual warehouse review — review shipment details to approve, revise time, or decline.'
                          : s === 'approved' || s === 'scheduled' ? 'Next: check truck in when it arrives at the yard.'
                          : s === 'checked_in' ? 'Truck is on-site. Dispatch to dock when ready to begin unloading.'
                          : s === 'in_progress' ? 'Record receiving results for each PO, then complete the appointment.'
                          : null;

                                                // Completed / cancelled - read-only state
                        if (s === 'completed') {
                          const isCorrectionEligible = (user?.role === 'admin' || user?.role === 'warehouse_manager') && data.appointment.completedAt != null;
                          const completedAtAge = data.appointment.completedAt ? (new Date().getTime() - new Date(data.appointment.completedAt).getTime()) / 60000 : Infinity;
                          const isCorrectionExpired = completedAtAge > 15;

                          return (
                            <div className="py-2 px-3.5 bg-emerald-50/80 dark:bg-emerald-950/20 border border-emerald-200/80 dark:border-emerald-800/80 rounded-lg flex items-center gap-2 text-xs mb-2 flex-wrap">
                              <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                              <span className="font-bold text-emerald-800 dark:text-emerald-300">Appointment completed</span>
                              {data.appointment.checkOutTime && (
                                <span className="text-emerald-600 dark:text-emerald-400">&middot; {formatTime(data.appointment.checkOutTime)}</span>
                              )}
                              
                              {isCorrectionEligible && (
                                <div className="ml-auto">
                                  <button 
                                      onClick={() => setShowCorrectionDialog(true)}
                                      disabled={isCorrectionExpired}
                                      title={isCorrectionExpired ? "Correction window expired (15 minutes)" : "Return to checked-in"}
                                      className="h-7 px-2.5 inline-flex items-center justify-center border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-400 dark:hover:bg-amber-900/50 text-xs font-semibold rounded transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                      <RotateCcw className="w-3 h-3 shrink-0 mr-1.5" />
                                      Correct Completion
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        }

                        if (s === 'cancelled') {
                          const rawNotes = String(data.appointment.notes || data.appointment.cancelReason || '');
                          const lowerNotes = rawNotes.toLowerCase();
                          const isCarrierCancelled = lowerNotes.includes('cancelled by carrier') || data.approvalHistory?.cancellation?.cancellationOrigin === 'carrier';
                          const isDeclined = !isCarrierCancelled && (lowerNotes.startsWith('declined') || data.approvalHistory?.cancellation?.cancellationOrigin === 'warehouse_declined');
                          const origApp = data.approvalHistory?.originalApproval;
                          const releasedDockNum = data.approvalHistory?.lastAssignedDockNumber || data.appointment.lastAssignedDockNumber;
                          return (
                            <div className="py-2.5 px-3.5 bg-red-50/70 dark:bg-red-950/20 border border-red-200/80 dark:border-red-800/80 rounded-lg space-y-1.5 text-xs">
                              <div className="flex items-center gap-2">
                                <XCircle className="w-4 h-4 text-red-500 shrink-0" />
                                <span className="font-semibold text-red-700 dark:text-red-400">
                                  {isCarrierCancelled ? 'Appointment cancelled by carrier' : isDeclined ? 'Appointment declined by warehouse' : 'Appointment cancelled by warehouse'}
                                </span>
                              </div>
                              {(origApp?.approvedAt || releasedDockNum) && (
                                <div className="pl-6 text-slate-600 dark:text-slate-300 space-y-0.5">
                                  {origApp?.approvedAt && (
                                    <div>
                                      Originally approved{origApp.approvedByActor ? ` by ${origApp.approvedByActor}` : ''} on {formatTime(origApp.approvedAt)}
                                      {origApp.dockNumber ? ` · Assigned Dock ${origApp.dockNumber}` : ''}
                                    </div>
                                  )}
                                  {Array.isArray(data.approvalHistory?.dockReassignments) && data.approvalHistory.dockReassignments.length > 0 && (
                                    <div>
                                      Dock reassignments: {data.approvalHistory.dockReassignments.map((r: any) => `Dock ${r.previousDockNumber || r.previousDockId || '?'} → Dock ${r.newDockNumber || r.newDockId || '?'}`).join(', ')}
                                    </div>
                                  )}
                                  {releasedDockNum && (
                                    <div>
                                      Dock {releasedDockNum} released upon cancellation (current dock assignment cleared)
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        }

                        const showPrimary =
                          s === 'requested' || s === 'pending' ||
                          s === 'approved' || s === 'scheduled' ||
                          s === 'checked_in' || s === 'in_progress';
                        if (!showPrimary) return null;

                        const hasDock = !!(data?.appointment?.dockId || data?.appointment?.dock_id);
                        const dockNum = data?.appointment?.dockNumber || data?.appointment?.dock_number;

                        return (
                          <div className="py-2.5 px-3.5 bg-slate-100/70 dark:bg-slate-800/60 rounded-lg border border-slate-200/80 dark:border-slate-700/80">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className={`w-2 h-2 rounded-full shrink-0 ${s === 'checked_in' ? 'bg-blue-500' : s === 'in_progress' ? 'bg-amber-500' : s === 'approved' || s === 'scheduled' ? 'bg-emerald-500' : 'bg-purple-500'}`} />
                                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                                  {stepMsg || (
                                    s === 'checked_in' ? 'Truck is on-site. Dispatch to dock when ready.' :
                                    s === 'in_progress' ? 'Unloading in progress. Record receiving results.' :
                                    s === 'approved' || s === 'scheduled' ? 'Scheduled & approved' : 'Request pending'
                                  )}
                                </span>
                              </div>

                              <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                                {(s === 'requested' || s === 'pending') && (
                                  <>
                                    <button onClick={() => { const defaultDock = reassignDockId || preferredDefaultDockId; if (!reassignDockId && defaultDock) setReassignDockId(defaultDock); setPendingConfirm({ action: 'approve', title: 'Confirm Approval', description: 'This will assign a dock, notify the carrier, and lock the time slot.', confirmLabel: 'Approve', confirmClass: 'bg-emerald-600 hover:bg-emerald-500 text-white', body: defaultDock ? { dockId: defaultDock } : undefined }); }} disabled={!!actionLoading || data?.po?.status === 'on_hold'} className="h-8 px-3 inline-flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-md transition-all disabled:opacity-50 shadow-sm" title={data?.po?.status === 'on_hold' ? 'Cannot approve an appointment for an on-hold PO' : undefined}>
                                      {actionLoading === 'approve' ? <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 shrink-0" />} Approve
                                    </button>
                                    <button onClick={() => { setShowReviseTime(true); setReviseDate(data.appointment.requestedDate || ''); setReviseTime(data.appointment.requestedTime || '09:00'); setReviseNote(''); }} disabled={!!actionLoading || showReviseTime} className="h-8 px-2.5 inline-flex items-center gap-1 text-xs font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-md transition-colors disabled:opacity-50 shadow-sm">
                                      <Clock className="w-3.5 h-3.5" /> Revise Time
                                    </button>
                                    <button onClick={() => setPendingConfirm({ action: 'reject', title: 'Decline Request?', description: 'This will reject the carrier\'s request and notify them.', confirmLabel: 'Decline', confirmClass: 'bg-red-600 hover:bg-red-500 text-white' })} disabled={!!actionLoading} className="h-8 px-2.5 inline-flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400 bg-white dark:bg-slate-900 border border-red-200 dark:border-red-800/60 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-md transition-colors disabled:opacity-50 shadow-sm">
                                      <XCircle className="w-3.5 h-3.5" /> Decline
                                    </button>
                                  </>
                                )}

                                {(s === 'approved' || s === 'scheduled') && (
                                  <button onClick={() => setPendingConfirm({ action: 'checkin', title: 'Confirm Check-In', description: 'This will start the dwell clock and mark the driver as arrived.', confirmLabel: 'Check In', confirmClass: 'bg-blue-600 hover:bg-blue-500 text-white' })} disabled={!!actionLoading || data?.po?.status === 'on_hold'} className="h-8 px-3 inline-flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-md transition-all disabled:opacity-50 shadow-sm" title={data?.po?.status === 'on_hold' ? 'Cannot check in an on-hold PO' : undefined}>
                                    {actionLoading === 'checkin' ? <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin" /> : <UserCheck className="w-3.5 h-3.5 shrink-0" />} Arrive
                                  </button>
                                )}

                                {s === 'approved' && (
                                  <button onClick={() => setPendingConfirm({ action: 'cancel', title: 'Cancel Appointment?', description: 'This will cancel the appointment and remove its future dock assignment. This action cannot be undone.', confirmLabel: 'Cancel Appointment', confirmClass: 'bg-red-600 hover:bg-red-500 text-white' })} disabled={!!actionLoading} className="h-8 px-2.5 inline-flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400 bg-white dark:bg-slate-900 border border-red-200 dark:border-red-800/60 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-md transition-colors disabled:opacity-50 shadow-sm">
                                    {actionLoading === 'cancel' ? <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin" /> : <XCircle className="w-3.5 h-3.5 shrink-0" />} Cancel Appointment
                                  </button>
                                )}

                                {s === 'checked_in' && (
                                  <>
                                    {hasDock ? (
                                      <button onClick={() => setPendingConfirm({ action: 'dispatch', title: 'Confirm Dispatch to Dock', description: `This will mark the truck as actively occupying dock${dockNum ? ' ' + dockNum : ''}.`, confirmLabel: 'Dispatch', confirmClass: 'bg-emerald-600 hover:bg-emerald-500 text-white' })} disabled={!!actionLoading} className="h-8 px-3 inline-flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-md transition-all disabled:opacity-50 shadow-sm">
                                        {actionLoading === 'dispatch' ? <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin" /> : <ArrowRightCircle className="w-3.5 h-3.5 shrink-0" />}
                                        Dispatch to Dock{dockNum ? ` ${dockNum}` : ''}
                                      </button>
                                    ) : (
                                      <span className="text-xs text-slate-500 dark:text-slate-400 italic py-1 pr-1">Assign a dock before dispatching</span>
                                    )}
                                    <button onClick={() => setPendingConfirm({ action: 'uncheckin', title: 'Undo Check-In?', description: 'This will return the truck to the approved/scheduled state and clear the check-in.', confirmLabel: 'Undo Check-In', confirmClass: 'bg-slate-600 hover:bg-slate-500 text-white' })} disabled={!!actionLoading} className="h-8 px-2.5 inline-flex items-center justify-center gap-1 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-medium rounded-md transition-all disabled:opacity-50 shadow-sm">
                                      <RotateCcw className="w-3.5 h-3.5 shrink-0" /> Undo Check-In
                                    </button>
                                  </>
                                )}

                                {s === 'in_progress' && (
                                  <>
                                    {!hasReceipt && data?.linkedPOs && data.linkedPOs.length > 0 && (
                                      <button type="button" onClick={() => startReceiptEditing('new')} className="h-8 px-3 inline-flex items-center justify-center gap-1.5 bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-semibold rounded-md transition-all shadow-sm">
                                        <ClipboardCheck className="w-3.5 h-3.5 shrink-0" /> Record Receipt
                                      </button>
                                    )}
                                    <button onClick={() => {
                                      const lpos = data?.linkedPOs ?? [];
                                      const willClose = lpos.filter((p: any) => { if (p.receiptStatus === 'complete') return true; if (p.receiptStatus === 'short' || p.receiptStatus === 'over' || p.receiptStatus === 'damaged' || p.receiptStatus === 'refused') return false; if (p.receivedPallets != null && p.expectedPallets != null && p.receivedPallets !== p.expectedPallets) return false; return true; });
                                      const remainOpen = lpos.filter((p: any) => { if (p.receiptStatus === 'short' || p.receiptStatus === 'over' || p.receiptStatus === 'damaged' || p.receiptStatus === 'refused') return true; if (p.receivedPallets != null && p.expectedPallets != null && p.receivedPallets !== p.expectedPallets && p.receiptStatus !== 'complete') return true; return false; });
                                      const followupScheduled = lpos.filter((p: any) => p.resolutionStatus === 'followup_created');
                                      const hasOpenIssues = remainOpen.length > 0;
                                      const missingReceiptCount = lpos.filter((p: any) => !p.receiptStatus).length;
                                      if (missingReceiptCount > 0) { setReceiptGateOpen(true); return; }
                                      const poPreview = lpos.length > 0 ? (
                                        <div className="mt-3 space-y-3 text-left text-sm">
                                          <div className="flex flex-wrap gap-2">
                                            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">{willClose.length} will close</span>
                                            {remainOpen.length > 0 && <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">{remainOpen.length} remain open</span>}
                                            {followupScheduled.length > 0 && <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400">{followupScheduled.length} follow-up</span>}
                                          </div>
                                          {willClose.length > 0 && (<div><div className="text-xs uppercase tracking-wider text-slate-400 dark:text-slate-500 font-bold mb-1">Will close</div><div className="space-y-1">{willClose.map((p: any) => { const diff = p.receivedPallets != null ? p.receivedPallets - (p.expectedPallets ?? 0) : null; return (<div key={p.id} className="flex items-center justify-between px-2.5 py-1.5 bg-emerald-50 dark:bg-emerald-950/20 rounded-md border border-emerald-200 dark:border-emerald-800/40"><span className="font-mono text-xs font-bold text-slate-800 dark:text-slate-200">{p.poNumber}</span><div className="flex items-center gap-2">{diff != null && diff === 0 && <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">✓ MATCH</span>}{p.receiptStatus && <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-600 text-white">{p.receiptStatus === 'complete' ? 'MATCH' : p.receiptStatus}</span>}</div></div>); })}</div></div>)}
                                          {remainOpen.length > 0 && (<div><div className="text-xs uppercase tracking-wider text-amber-500 dark:text-amber-400 font-bold mb-1">Remain open</div><div className="space-y-1">{remainOpen.map((p: any) => { const diff = p.receivedPallets != null ? p.receivedPallets - (p.expectedPallets ?? 0) : null; return (<div key={p.id} className="flex items-center justify-between px-2.5 py-1.5 bg-amber-50 dark:bg-amber-950/20 rounded-md border border-amber-200 dark:border-amber-800/40"><span className="font-mono text-xs font-bold text-slate-800 dark:text-slate-200">{p.poNumber}</span><div className="flex items-center gap-2">{diff != null && diff !== 0 && <span className={`text-xs font-bold ${diff < 0 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>{diff < 0 ? diff : `+${diff}`}</span>}{p.receiptStatus && <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${p.receiptStatus === 'short' ? 'bg-red-600' : p.receiptStatus === 'damaged' || p.receiptStatus === 'refused' ? 'bg-red-700' : 'bg-amber-600'} text-white`}>{p.receiptStatus}</span>}</div></div>); })}</div></div>)}
                                          {followupScheduled.length > 0 && (<div><div className="text-xs uppercase tracking-wider text-cyan-500 dark:text-cyan-400 font-bold mb-1">Follow-up scheduled</div><div className="space-y-1">{followupScheduled.map((p: any) => { const fuRef = followupPoMap[p.id] || (p.followupPoNumber ? { poNumber: p.followupPoNumber } : null); return (<div key={p.id} className="flex items-center justify-between px-2.5 py-1.5 bg-cyan-50 dark:bg-cyan-950/20 rounded-md border border-cyan-200 dark:border-cyan-800/40"><span className="font-mono text-xs font-bold text-slate-800 dark:text-slate-200">{p.poNumber}</span>{fuRef && <span className="text-xs font-mono font-bold text-cyan-600 dark:text-cyan-400">→ {fuRef.poNumber}</span>}</div>); })}</div></div>)}
                                        </div>
                                      ) : undefined;
                                      const confirmPayload = {
                                        action: 'checkout' as const, title: 'Confirm Completion',
                                        description: hasOpenIssues ? `This will complete the appointment and release its dock. Eligible linked records may be finalized automatically. ${remainOpen.length} PO${remainOpen.length !== 1 ? 's' : ''} will remain open due to unresolved shortages.` : 'This will complete the appointment and release its dock. Eligible linked records may be finalized automatically.',
                                        confirmLabel: hasOpenIssues ? `Complete (${remainOpen.length} PO${remainOpen.length !== 1 ? 's' : ''} remain open)` : 'Complete',
                                        confirmClass: 'bg-slate-700 hover:bg-slate-600 text-white', content: poPreview,
                                      };
                                      const unresolvedShortage = remainOpen.filter((p: any) => p.resolutionStatus !== 'followup_created');
                                      if (unresolvedShortage.length > 0) { setShortageWarningPending({ confirmPayload }); return; }
                                      setPendingConfirm(confirmPayload);
                                    }} disabled={!!actionLoading} className={`h-8 px-3 inline-flex items-center justify-center gap-1.5 text-xs font-semibold rounded-md transition-all disabled:opacity-50 ${hasReceipt ? 'bg-slate-700 hover:bg-slate-600 dark:bg-slate-600 dark:hover:bg-slate-500 text-white shadow-sm' : 'border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300'}`}>
                                      {actionLoading === 'checkout' ? <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin" /> : <LogOut className="w-3.5 h-3.5 shrink-0" />} Complete
                                    </button>
                                    <button onClick={() => setPendingConfirm({ action: 'undispatch', title: 'Undo dispatch?', description: 'This will remove the truck from the dock and return it to the checked-in state.', confirmLabel: 'Undo Dispatch', confirmClass: 'bg-slate-600 hover:bg-slate-500 text-white' })} disabled={!!actionLoading} className="h-8 px-2.5 inline-flex items-center justify-center gap-1 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-medium rounded-md transition-all disabled:opacity-50 shadow-sm">
                                      <RotateCcw className="w-3.5 h-3.5 shrink-0" /> Undo Dispatch
                                    </button>
                                  </>
                                )}

                                {!['completed', 'cancelled', 'declined'].includes(s) && (
                                  <button onClick={() => { setShowDockReassign(prev => !prev); setReassignDockId(''); }} disabled={!!actionLoading || showDockReassign} className="h-8 px-2.5 inline-flex items-center justify-center gap-1 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-medium rounded-md transition-all disabled:opacity-50 shadow-sm">
                                    <MapPin className="w-3.5 h-3.5 shrink-0" />
                                    {hasDock ? 'Reassign Dock' : 'Assign Dock'}
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Dock reassignment picker (inline) */}
                            {showDockReassign && !['completed', 'cancelled', 'declined'].includes(data.appointment.status) && (
                              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                                <select value={reassignDockId} onChange={(e) => setReassignDockId(e.target.value)} className="field text-sm flex-1">
                                  <option value="">Select dock...</option>
                                  {availableDocks.map((d: any) => {
                                    const capLabel = formatDockCapabilities(d);
                                    const isCurrent = d.id === (data.appointment.dockId || data.appointment.dock_id);
                                    const isConflicting = conflictingDockIds.has(d.id);
                                    return (
                                      <option key={d.id} value={d.id} disabled={isCurrent || isConflicting}>
                                        Dock {d.dockNumber}{capLabel ? ` — ${capLabel}` : ''}{d.type ? ` (${d.type})` : ''}{isCurrent ? ' — current' : isConflicting ? ' — Occupied (overlapping slot)' : ''}
                                      </option>
                                    );
                                  })}
                                </select>
                                <button disabled={!reassignDockId || reassignLoading} onClick={async () => {
                                  if (!reassignDockId || !token || !appointmentId) return;
                                  if (data?.appointment?.status === 'pending' || data?.appointment?.status === 'requested') {
                                    setShowDockReassign(false);
                                    setPendingConfirm({
                                      action: 'approve',
                                      title: 'Confirm Approval',
                                      description: 'This will assign the selected dock, notify the carrier, and lock the time slot.',
                                      confirmLabel: 'Approve',
                                      confirmClass: 'bg-emerald-600 hover:bg-emerald-500 text-white',
                                      body: { dockId: reassignDockId },
                                    });
                                    return;
                                  }
                                  setReassignLoading(true);
                                  try {
                                    const res = await fetch(`/api/appointments/${appointmentId}/reassign-dock`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ dockId: reassignDockId }) });
                                    if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Reassignment failed'); }
                                    toast.success('Dock reassigned — Board and calendar updated', { duration: 4000 });
                                    setShowDockReassign(false); setReassignDockId('');
                                    await mutate(); notifyActionSuccess();
                                  } catch (err: any) { toast.error(err.message || 'Failed to reassign dock'); } finally { setReassignLoading(false); }
                                }} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-lg transition-all disabled:opacity-50">
                                  {reassignLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Confirm'}
                                </button>
                                <button onClick={() => { setShowDockReassign(false); setReassignDockId(''); }} className="text-xs text-slate-400 hover:text-slate-600">Cancel</button>
                              </div>
                            )}

                            {/* Revise time inline panel */}
                            {showReviseTime && (data.appointment.status === 'requested' || data.appointment.status === 'pending') && (
                              <div className="w-full mt-2 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg p-3 space-y-2 animate-in slide-in-from-top duration-200">
                                <p className="text-amber-700 dark:text-amber-200 text-xs font-medium">Propose a new time for this request. The carrier will be notified and the request will remain pending.</p>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">New Date</label>
                                    <input type="date" value={reviseDate} onChange={(e) => setReviseDate(e.target.value)} className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg p-1.5 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500" />
                                    {(() => {
                                        const linkedPOs = (data.linkedPOs || []) as any[];
                                        const startAt = data.appointment.startAt ? new Date(data.appointment.startAt) : null;
                                        if (!startAt) return null;
                                        const latePOs = linkedPOs.filter((po: any) => po.dueDate && new Date(po.dueDate) < startAt);
                                        if (latePOs.length === 0) return null;
                                        const earliest = latePOs.reduce((min: Date, po: any) => { const d = new Date(po.dueDate); return d < min ? d : min; }, new Date(latePOs[0].dueDate));
                                        const hasCritical = latePOs.some((po: any) => po.priority === 'critical');
                                        const hasUrgent = latePOs.some((po: any) => po.priority === 'urgent');
                                        const color = hasCritical ? 'text-red-500' : hasUrgent ? 'text-orange-500' : 'text-amber-600 dark:text-amber-400';
                                        return <p className={`text-xs font-semibold ${color} mt-1`}>Required arrival: {earliest.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}</p>;
                                    })()}
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">New Time</label>
                                    <input type="time" value={reviseTime} onChange={(e) => setReviseTime(e.target.value)} className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg p-1.5 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500" />
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Note (optional)</label>
                                  <textarea value={reviseNote} onChange={(e) => setReviseNote(e.target.value)} placeholder="Reason for revision..." className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg p-1.5 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none h-12" />
                                </div>
                                <div className="flex gap-2 justify-end">
                                  <button onClick={() => setShowReviseTime(false)} className="px-3 py-1 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">Cancel</button>
                                  <button onClick={handleReviseTime} disabled={reviseLoading || !reviseDate || !reviseTime} className="px-3 py-1 bg-amber-600 hover:bg-amber-500 text-white text-xs font-medium rounded-lg transition-all disabled:opacity-50 flex items-center gap-1.5">
                                    {reviseLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Send Revision
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* === (B) RECEIVING RECONCILIATION / UNLOADING RESULTS === */}
                      {data?.linkedPOs && data.linkedPOs.length > 0 && (() => {
                        const totalPOs = data.linkedPOs.length;
                        const shortCount = data.linkedPOs.filter((po: any) => po.receiptStatus === 'short').length;
                        const overCount = data.linkedPOs.filter((po: any) => po.receiptStatus === 'over').length;
                        const damagedCount = data.linkedPOs.filter((po: any) => po.receiptStatus === 'damaged').length;
                        const refusedCount = data.linkedPOs.filter((po: any) => po.receiptStatus === 'refused').length;
                        const unresolvedCount = data.linkedPOs.filter((po: any) => (po.receiptStatus === 'short' || po.receiptStatus === 'damaged' || po.receiptStatus === 'refused') && (!po.resolutionStatus || po.resolutionStatus === 'unresolved')).length;
                        const pendingCount = data.linkedPOs.filter((po: any) => !po.receiptStatus).length;
                        return (
                          <div className="border-t border-slate-200 dark:border-slate-800 pt-3">
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2.5">
                              <div className="flex items-center gap-2">
                                <ClipboardCheck className="w-4 h-4 text-slate-500 dark:text-slate-400 shrink-0" />
                                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">Receiving Reconciliation</h3>
                                <span className="text-xs text-slate-400">&middot;</span>
                                <span className="text-xs text-slate-500 dark:text-slate-400">{totalPOs} PO{totalPOs !== 1 ? 's' : ''}</span>
                                {pendingCount > 0 && <span className="text-xs font-medium text-amber-600 dark:text-amber-400">({pendingCount} pending)</span>}
                              </div>
                              {['checked_in', 'in_progress', 'completed'].includes(data.appointment?.status ?? '') && user?.role !== 'carrier' && (() => {
                                const hasExistingReceipt = data.linkedPOs.some((po: any) => po.receiptStatus);
                                if (!hasExistingReceipt && !receiptEditing) return null;
                                return (
                                  <button type="button" onClick={() => {
                                    if (receiptEditing) {
                                      setReceiptEditing(false);
                                    } else {
                                      startReceiptEditing('edit');
                                    }
                                  }} className={`text-xs font-semibold px-2.5 py-1 rounded transition-colors ${receiptEditing ? 'bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300' : 'text-cyan-600 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/20'}`}>
                                    {receiptEditing ? 'Cancel' : 'Edit Receipt'}
                                  </button>
                                );
                              })()}
                            </div>
                            {(shortCount > 0 || overCount > 0 || damagedCount > 0 || refusedCount > 0 || unresolvedCount > 0) && !receiptEditing && (
                              <div className="mb-2.5 flex flex-wrap items-center gap-1.5">
                                {shortCount > 0 && <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800">{shortCount} short</span>}
                                {overCount > 0 && <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800">{overCount} over</span>}
                                {damagedCount > 0 && <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800">{damagedCount} damaged</span>}
                                {refusedCount > 0 && <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800">{refusedCount} refused</span>}
                                {unresolvedCount > 0 && <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800">{unresolvedCount} needs follow-up</span>}
                              </div>
                            )}
                            <div className="space-y-1.5">
                              {data.linkedPOs.map((po: any) => {
                                const draft = receiptDraft[po.id];
                                const expected = po.expectedPallets;
                                const hasExpected = expected != null;
                                const received = receiptEditing ? (draft?.received ?? 0) : po.receivedPallets;
                                const diff = (hasExpected && received != null) ? received - expected : null;
                                const isShort = diff != null && diff < 0;
                                const isMatch = diff === 0 && received != null;
                                const isPending = !po.receiptStatus && !receiptEditing;
                                const statusBorder = po.receiptStatus === 'refused' ? 'border-red-200 dark:border-red-800/50' : po.receiptStatus === 'short' || po.receiptStatus === 'over' || po.receiptStatus === 'damaged' ? 'border-amber-200 dark:border-amber-800/50' : 'border-slate-200/80 dark:border-slate-700/60';
                                const statusBg = po.receiptStatus === 'refused' ? 'bg-red-50/50 dark:bg-red-950/20' : po.receiptStatus === 'short' || po.receiptStatus === 'over' || po.receiptStatus === 'damaged' ? 'bg-amber-50/50 dark:bg-amber-950/20' : 'bg-slate-50/60 dark:bg-slate-800/40';
                                return (
                                  <div key={po.id} className={`rounded-md border ${statusBorder} ${statusBg} text-xs transition-all`}>
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between px-3 py-2 gap-2 border-b border-inherit last:border-0">
                                      <div className="flex items-center gap-2 min-w-0">
                                        <Package className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 shrink-0" />
                                        <span className="font-mono font-bold text-slate-900 dark:text-slate-100 truncate">{po.poNumber}</span>
                                        {po.clientName && <span className="text-slate-500 dark:text-slate-400 truncate">({po.clientName})</span>}
                                      </div>
                                      <div className="flex items-center gap-3 shrink-0">
                                        <div className="flex items-center gap-2 font-mono text-xs text-slate-500 dark:text-slate-400">
                                          <span>Exp: <strong className="text-slate-700 dark:text-slate-200 font-semibold">{hasExpected ? expected : 'N/A'}</strong></span>
                                          <span className="text-slate-300 dark:text-slate-600">&middot;</span>
                                          <span>Rcv: {receiptEditing ? (
                                            <input type="number" min="0" className="w-14 bg-white dark:bg-slate-950 border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0.5 text-xs font-bold font-mono text-center focus:outline-none focus:ring-1 focus:ring-cyan-400" value={draft?.received ?? 0} onChange={(e) => { const val = Math.max(0, parseInt(e.target.value) || 0); setReceiptDraft(prev => ({ ...prev, [po.id]: { ...prev[po.id], received: val } })); }} />
                                          ) : (
                                            <strong className="text-slate-800 dark:text-slate-200 font-bold">{received != null ? received : '-'}</strong>
                                          )}</span>
                                          {!receiptEditing && (
                                            <>
                                              <span className="text-slate-300 dark:text-slate-600">&middot;</span>
                                              <span>Var: <strong className={`font-semibold ${diff != null ? (isMatch ? 'text-emerald-600 dark:text-emerald-400' : isShort ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400') : 'text-slate-400'}`}>
                                                {diff != null ? (isMatch ? '0' : diff > 0 ? `+${diff}` : diff) : '—'}
                                              </strong></span>
                                            </>
                                          )}
                                        </div>
                                        {!receiptEditing && (po.receiptStatus || po.receiptNotes) ? <POReceiptOutcomeBadge receiptStatus={po.receiptStatus} receiptNotes={po.receiptNotes} /> : isPending ? <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400">PENDING</span> : null}
                                      </div>
                                    </div>
                                    {hasPOMetadata(po) && (<div className="px-3 py-1 border-b border-inherit"><div className="text-muted-foreground">{formatPOMetadata(po)}</div>{po.itemDescription && <div className="text-slate-600 dark:text-slate-300 mt-0.5">Item: {po.itemDescription}</div>}</div>)}
                                    {data?.dockCapabilities && (data.dockCapabilities.temperatureZone || data.dockCapabilities.handlingCapabilities) && hasPOMetadata(po) && (() => {
                                      const compat = summarizeCompatibilityStatus(data.dockCapabilities, [{ requiredTemperature: po.requiredTemperature, handlingType: po.handlingType }]);
                                      return compat.compatibilityLabel ? (<div className="px-3 py-1 border-b border-inherit"><span className="font-medium text-amber-600 dark:text-amber-400">⚠ {compat.compatibilityLabel}</span></div>) : null;
                                    })()}
                                    {receiptEditing && (
                                      <div className="px-3 py-2 space-y-2">
                                        <div className="flex items-center gap-2">
                                          <select className="bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-md px-2.5 py-1 font-medium" value={draft?.status ?? 'complete'} onChange={(e) => setReceiptDraft(prev => ({ ...prev, [po.id]: { ...prev[po.id], status: e.target.value } }))}><option value="complete">Complete</option><option value="short">Short</option><option value="over">Over</option></select>
                                          <input type="text" placeholder="Notes (optional)" className="flex-1 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-md px-2.5 py-1" value={draft?.notes ?? ''} onChange={(e) => setReceiptDraft(prev => ({ ...prev, [po.id]: { ...prev[po.id], notes: e.target.value } }))} />
                                        </div>
                                        {totalPOs > 1 && <p className="text-muted-foreground">Exceptions are recorded per PO.</p>}
                                        <div>
                                          <label className="text-muted-foreground font-medium">Receiving exception (optional)</label>
                                          <select className="mt-0.5 w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-md px-2.5 py-1" value={draft?.exception ?? ''} onChange={(e) => setReceiptDraft(prev => ({ ...prev, [po.id]: { ...prev[po.id], exception: e.target.value } }))}>
                                            <option value="">None</option>
                                            {EXCEPTION_OPTIONS.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                                          </select>
                                          {draft?.exception && EXCEPTION_EXPLANATIONS[draft.exception as POExceptionType] && (<p className="text-muted-foreground mt-1">{EXCEPTION_EXPLANATIONS[draft.exception as POExceptionType]}</p>)}
                                        </div>
                                        {draft?.exception && (<div className="bg-amber-50 dark:bg-amber-900/20 rounded-md px-2.5 py-1.5"><p className="text-amber-700 dark:text-amber-400">⚠ Receiving exception recorded: {formatPOException(draft.exception)}</p></div>)}
                                      </div>
                                    )}
                                    {receiptEditing && (() => {
                                      return (
                                        <details className="mx-3 mb-2 group">
                                          <summary className="font-medium text-slate-500 cursor-pointer list-none flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-300">
                                            <ChevronDown className="w-3 h-3 group-open:-rotate-180 transition-transform" />
                                            What happens after saving?
                                          </summary>
                                          <div className="mt-1.5 bg-slate-50 dark:bg-slate-800/40 rounded-md px-2.5 py-1.5 space-y-0.5 border border-slate-200 dark:border-slate-700 text-muted-foreground">
                                            <p><strong>Complete:</strong> PO closes automatically.</p>
                                            <p><strong>Short:</strong> PO stays open until resolved.</p>
                                            <p><strong>Over:</strong> PO closes automatically.</p>
                                          </div>
                                        </details>
                                      );
                                    })()}
                                    {!receiptEditing && po.receiptNotes && (() => { const cleanNote = stripExceptionFromNotes(po.receiptNotes || '').trim(); if (!cleanNote) return null; return (<div className="px-3 py-1 text-[11px] text-slate-500 dark:text-slate-400 italic border-t border-inherit/40">Note: "{cleanNote}"</div>); })()}
                                    {!receiptEditing && (po.receiptStatus === 'short' || po.receiptStatus === 'damaged' || po.receiptStatus === 'refused') && (
                                      <div className="px-3 py-2 border-t border-inherit">
                                        {(!po.resolutionStatus || po.resolutionStatus === 'unresolved') ? (
                                          isOperationsTierUser(user) ? (
                                            <button type="button" disabled={followupCreating === po.id} onClick={async () => {
                                              setFollowupCreating(po.id);
                                              try {
                                                const shortCount2 = po.shortageCount ?? Math.max(0, (po.expectedPallets ?? 0) - (po.receivedPallets ?? 0));
                                                const res = await fetch(`/api/warehouse-pos/${po.id}/create-followup`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ shortageCount: shortCount2, appointmentId: data?.appointment?.id }) });
                                                if (!res.ok) throw new Error((await res.json()).error);
                                                const result = await res.json();
                                                setFollowupPoMap(prev => ({ ...prev, [po.id]: { poNumber: result.followupPO.customerPoNumber, poId: result.followupPO.id } }));
                                                toast.success(`Follow-up PO ${result.followupPO.customerPoNumber} created`); mutate();
                                              } catch (err: any) { console.error('Follow-up creation error:', err); toast.error(err.message || 'Failed to create follow-up PO'); } finally { setFollowupCreating(null); }
                                            }} className="font-semibold text-cyan-600 dark:text-cyan-400 hover:text-cyan-500 transition-colors flex items-center gap-1.5 disabled:opacity-50">
                                              {followupCreating === po.id ? (<><Loader2 className="w-3 h-3 animate-spin" /> Creating...</>) : (<><ArrowRightCircle className="w-3.5 h-3.5" /> Create Follow-up PO</>)}
                                            </button>
                                          ) : (
                                            <span
                                              data-testid="appointment-detail-followup-po-upgrade-notice"
                                              className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-1.5"
                                            >
                                              Shortage follow-up PO generation (-R1) requires the Operations plan ($199/mo). Standard PO status remains open until resolved.
                                            </span>
                                          )
                                        ) : po.resolutionStatus === 'followup_created' ? (() => {
                                          const fuRef = followupPoMap[po.id] || (po.followupPoNumber ? { poNumber: po.followupPoNumber, poId: po.followupPoId } : null);
                                          return (<div className="flex items-center justify-between py-0.5"><span className="text-emerald-700 dark:text-emerald-400 font-semibold flex items-center gap-1.5 text-[11px]"><CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> Follow-up PO created</span>{fuRef && (<button type="button" onClick={(e) => { e.stopPropagation(); closeAppointmentDetail(); setLocation('/orders'); }} className="inline-flex items-center gap-1 font-bold text-[11px] text-cyan-700 dark:text-cyan-400 bg-white dark:bg-slate-900 px-2 py-0.5 rounded border border-cyan-200 dark:border-cyan-800 hover:bg-cyan-50 transition-colors" title={`Open ${fuRef.poNumber} in Purchase Orders`}><Package className="w-3 h-3" /><span>{fuRef.poNumber}</span><ExternalLink className="w-2.5 h-2.5" /></button>)}</div>);
                                        })() : po.resolutionStatus === 'written_off' ? (<span className="text-slate-500 dark:text-slate-400 font-medium line-through">Written off</span>) : po.resolutionStatus === 'resolved' ? (<span className="text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5" /> Resolved</span>) : null}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            {receiptEditing && (
                              <div className="mt-2.5 px-3 py-2 bg-cyan-50 dark:bg-cyan-900/20 rounded-md border border-cyan-200 dark:border-cyan-800/50 flex items-center justify-between">
                                <span className="text-xs text-cyan-700 dark:text-cyan-300 font-medium">{Object.keys(receiptDraft).length} PO{Object.keys(receiptDraft).length !== 1 ? 's' : ''} edited</span>
                                <button type="button" disabled={receiptSaving} onClick={async () => {
                                  setReceiptSaving(true);
                                  try {
                                    const lines = Object.entries(receiptDraft).map(([poId, d]) => {
                                      const ex = d.exception; const exLabel = ex ? formatPOException(ex) : null;
                                      let cleanNotes = stripExceptionFromNotes(d.notes || '');
                                      const effectiveNotes = exLabel ? [cleanNotes, `Exception: ${exLabel}`].filter(Boolean).join('\n') : cleanNotes;
                                      return { poId, receivedPalletCount: d.received, receiptStatus: d.status as any, receiptNotes: effectiveNotes || undefined };
                                    });
                                    const res = await fetch(`/api/appointments/${data.appointment.id}/receipt`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ lines }) });
                                    if (!res.ok) throw new Error((await res.json()).error);
                                    setReceiptEditing(false); mutate();
                                  } catch (err: any) { console.error('Receipt save error:', err); alert(err.message || 'Failed to save receipt'); } finally { setReceiptSaving(false); }
                                }} className="text-xs font-bold px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 active:bg-cyan-700 text-white rounded-md transition-all disabled:opacity-50 flex items-center gap-1.5">
                                  {receiptSaving ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving...</> : <><ClipboardCheck className="w-3.5 h-3.5" /> Save Receipt</>}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      

                      {/* === (D) SHIPMENT & CARRIER === */}
                      <div className="border-t border-slate-200 dark:border-slate-800 pt-3">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-2">Shipment &amp; Carrier</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-xs">
                          <div className="flex items-center gap-1.5 truncate"><span className="text-slate-500 dark:text-slate-400">Carrier:</span><span className="font-semibold text-slate-800 dark:text-slate-200 truncate">{data.appointment.carrierName || 'Unassigned'}</span></div>
                          <div className="flex items-center gap-1.5 truncate"><span className="text-slate-500 dark:text-slate-400">MC #:</span><span className="font-mono font-medium text-slate-800 dark:text-slate-200">{data.appointment.carrierMcNumber || '-'}</span></div>
                          <div className="flex items-center gap-1.5 truncate"><span className="text-slate-500 dark:text-slate-400">Truck #:</span><span className="font-mono font-medium text-slate-800 dark:text-slate-200">{data.appointment.truckNumber || '-'}</span></div>
                          <div className="flex items-center gap-1.5 truncate"><span className="text-slate-500 dark:text-slate-400">Trailer #:</span><span className="font-mono font-medium text-slate-800 dark:text-slate-200">{data.appointment.trailerNumber || '-'}</span></div>
                          <div className="flex items-center gap-1.5 truncate"><span className="text-slate-500 dark:text-slate-400">Equipment:</span><span className="font-medium text-slate-800 dark:text-slate-200">{data.appointment.trailerType || 'Standard'}</span></div>
                          {data.loadRef?.loadNumber && (<div className="flex items-center gap-1.5 truncate"><span className="text-slate-500 dark:text-slate-400">Load:</span><span className="font-mono font-semibold text-cyan-600 dark:text-cyan-400">{data.loadRef.loadNumber}</span></div>)}
                          {data?.linkedPOs && data.linkedPOs.length > 0 && (<div className="flex items-center gap-1.5 truncate"><span className="text-slate-500 dark:text-slate-400">Pallets:</span><span className="font-semibold text-slate-800 dark:text-slate-200">{data.linkedPOs.reduce((s: number, po: any) => s + (po.expectedPallets ?? 0), 0)} expected</span></div>)}
                        </div>
                        <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800/50">
                          <button type="button" onClick={() => setMetadataExpanded(prev => !prev)} className="flex items-center gap-1 text-[11px] font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors">
                            <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${metadataExpanded ? '' : '-rotate-90'}`} />
                            <span>{metadataExpanded ? 'Hide' : 'Show'} more shipment details</span>
                          </button>
                          {metadataExpanded && (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-xs mt-2 text-slate-600 dark:text-slate-300">
                              <div className="flex items-center gap-1.5 truncate"><span className="text-slate-500 dark:text-slate-400">Warehouse:</span><span className="font-medium">{user?.role === 'carrier' ? data.appointment.warehouseName : 'Current Warehouse'}</span></div>
                              <div className="flex items-center gap-1.5 truncate"><span className="text-slate-500 dark:text-slate-400">Driver:</span><span className="font-medium">{data.appointment.driverName || '-'}</span></div>
                              {data.appointment.notes && (<div className="col-span-2 sm:col-span-3 flex items-start gap-1.5 mt-1"><span className="text-slate-500 dark:text-slate-400 shrink-0">Notes:</span><span className="font-medium italic">{data.appointment.notes}</span></div>)}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* === (C) DETENTION NOTICE === */}
                      {detentionData.detentionHours > 0 && (
                        <div className="border-t border-slate-200 dark:border-slate-800 pt-2.5">
                          <div className="flex items-center gap-1.5 py-1 px-2.5 rounded bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-800/30 text-xs text-amber-800 dark:text-amber-300">
                            <AlertTriangle className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400 shrink-0" />
                            <span className="font-semibold">Detention notice &middot;</span>
                            <span>Free time exceeded by <strong className="font-mono font-bold">{(() => {
                              const hrs = detentionData.detentionHours;
                              const h = Math.floor(hrs);
                              const m = Math.round((hrs - h) * 60);
                              return h > 0 && m > 0 ? `${h}h ${m}m` : h > 0 ? `${h}h` : `${m}m`;
                            })()}</strong>.</span>
                          </div>
                        </div>
                      )}

                      {/* === (E) ACTIVITY TIMELINE === */}
                      <AppointmentTimelineSection
                        appointment={data.appointment}
                        linkedPOs={data?.linkedPOs}
                        warehouseTimeZone={data?.warehouseTimeZone}
                        defaultExpanded={data.appointment.status === 'cancelled'}
                        approvalHistory={data?.approvalHistory}
                        activityHistory={data?.activityHistory}
                      />

                      {/* === (G) FOLLOW-UP / OUTCOME === */}
                      {user?.role !== 'carrier' && (data?.followUps?.length > 0 || data.appointment.status === 'completed') && (
                        <div className="border-t border-slate-200 dark:border-slate-800 pt-3">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">Follow-Up &amp; Outcome</h3>
                            <button onClick={() => setIsFollowUpOpen(true)} className="text-xs font-semibold px-2 py-0.5 rounded text-cyan-600 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 transition-colors">+ Add Follow-up</button>
                          </div>
                          {data?.appointment?.outcome && !isOutcomeEditing ? (
                            <div className="mb-3">
                              <div className="flex items-center justify-between py-1.5 px-3 rounded-md bg-slate-50 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-700/60 text-xs">
                                <div className="flex items-center gap-2">
                                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                                  <span className="font-semibold text-slate-700 dark:text-slate-200">Outcome: <span className="capitalize font-bold">{data.appointment.outcome.replace(/_/g, ' ')}</span></span>
                                  {data.appointment.outcomeAt && (
                                    <span className="text-slate-400 dark:text-slate-500 text-[11px]">&middot; {formatTime(data.appointment.outcomeAt)}</span>
                                  )}
                                </div>
                                <button onClick={() => setIsOutcomeEditing(true)} className="text-xs font-medium text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">Edit</button>
                              </div>
                              {data.appointment.outcomeNotes && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 px-1 italic">"{data.appointment.outcomeNotes}"</p>}
                            </div>
                          ) : (
                            <div className={`mb-3 ${isOutcomeEditing ? 'bg-slate-50 dark:bg-slate-800/40 p-3 rounded-md border border-slate-200 dark:border-slate-700' : ''}`}>
                              {!isOutcomeEditing ? (
                                <button onClick={() => setIsOutcomeEditing(true)} className="w-full py-2 border border-dashed border-slate-300 dark:border-slate-700 rounded-md text-xs font-medium text-slate-500 hover:border-purple-500 hover:text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-colors flex items-center justify-center gap-1.5"><Flag className="w-3.5 h-3.5" /> Record Final Outcome</button>
                              ) : (
                                <div className="space-y-2.5">
                                  <div className="flex items-center justify-between"><span className="text-xs font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400">Set Outcome</span><button onClick={() => setIsOutcomeEditing(false)} className="text-xs text-slate-400 hover:text-slate-600">Cancel</button></div>
                                  <select value={outcomeType} onChange={(e) => setOutcomeType(e.target.value)} className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded px-2.5 py-1 text-xs font-medium"><option value="confirmed_issue">Confirmed Issue</option><option value="false_alarm">False Alarm</option><option value="carrier_updated_eta">Carrier Updated ETA</option><option value="rescheduled">Rescheduled</option><option value="docs_provided">Docs Provided</option><option value="cancelled">Cancelled</option><option value="other">Other</option></select>
                                  <textarea value={outcomeNotes} onChange={(e) => setOutcomeNotes(e.target.value)} placeholder="Add resolution notes..." className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded px-2.5 py-1 min-h-[50px] text-xs" />
                                  <div className="flex justify-end"><button onClick={saveOutcome} disabled={outcomeSubmitting} className="text-xs font-bold px-3 py-1 bg-purple-600 hover:bg-purple-500 text-white rounded transition-colors disabled:opacity-50 flex items-center gap-1">{outcomeSubmitting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save Outcome'}</button></div>
                                </div>
                              )}
                            </div>
                          )}
                          {data?.followUps && data.followUps.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800/60">
                              <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Follow-up Tasks</h4>
                              <div className="space-y-1.5">
                                {data.followUps.map((task: any) => (
                                  <div key={task.id} className="flex items-start gap-2 text-xs p-2 bg-slate-50 dark:bg-slate-800/50 rounded border border-slate-200/60 dark:border-slate-700/60">
                                    <div className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${task.status === 'completed' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                                    <div className="flex-1 min-w-0"><div className="font-semibold text-slate-800 dark:text-slate-200 capitalize truncate">{task.type.replace(/_/g, ' ')}</div><div className="text-slate-600 dark:text-slate-400 text-xs mt-0.5">{task.notes}</div><div className="text-[11px] text-slate-400 mt-1 flex gap-2"><span>Due: {task.dueDate ? new Date(task.dueDate).toLocaleDateString(undefined, { timeZone: 'UTC' }) : 'None'}</span>{task.status !== 'completed' && <span className="text-amber-500 font-medium">Pending</span>}</div></div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Old duplicate sections removed — all actions and receiving are in the top (A)-(G) sections above */}

                    </div>
                )}
                {/* DOCUMENTS TAB */}
                {activeTab === 'documents' && (
                  <DocumentsTabContent
                    appointment={data?.appointment}
                    documents={data?.documents || []}
                    poDocuments={data?.poDocuments || []}
                    uploading={uploading}
                    onFileUpload={handleFileUpload}
                    formatTime={formatTime}
                  />
                )}


                {/* MESSAGES TAB */}
                {activeTab === 'messages' && (
                  <MessagesTabContent
                    messages={data?.messages || []}
                    userId={user?.id}
                    newMessage={newMessage}
                    isSending={isSending}
                    onNewMessageChange={setNewMessage}
                    onSendMessage={sendMessage}
                    formatTime={formatTime}
                  />
                )}

                {/* NOTES TAB */}
                {activeTab === 'notes' && (
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <textarea
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        placeholder="Add an internal note..."
                        className="w-full field min-h-[100px]"
                      />
                      <div className="flex justify-end">
                        <button onClick={addNote} disabled={!newNote.trim() || isSaving} className="btn btn-primary">
                          {isSaving ? 'Saving...' : 'Save Note'}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {data?.notes?.map((note: Note) => (
                        <div
                          key={note.id}
                          className="p-4 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-900/30 rounded-lg"
                        >
                          <p className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap">{note.content}</p>
                          <div className="text-xs text-slate-500 mt-2">{formatTime(note.createdAt)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Outcome & Follow-up Dialogs */}
          {user?.role !== 'carrier' && (
            <CreateFollowUpDialog
              isOpen={isFollowUpOpen}
              onClose={() => setIsFollowUpOpen(false)}
              appointmentId={appointmentId || ''}
              onSuccess={() => { mutate(); toast.success('Follow-up created'); }}
            />
          )}

          {/* Late checkout time confirmation modal */}
          <LateTimeModal
            open={lateCheckoutPending}
            title="Late Check-Out — Confirm Actual Time"
            description="This check-out is more than 2 hours past the scheduled end. Please confirm the actual departure time from the dock."
            defaultTime={new Date().toISOString()}
            warehouseTimeZone={data?.warehouseTimeZone}
            onConfirm={(val) => {
              setLateCheckoutPending(false);
              let overrideTime;
              if (val.includes('T') && !val.endsWith('Z')) {
                  const [d, t] = val.split('T');
                  overrideTime = { actualDate: d, actualTime: t };
              } else {
                  overrideTime = { checkOutTime: val };
              }
              runAction('checkout', overrideTime);
            }}
            onCancel={() => setLateCheckoutPending(false)}
            isSubmitting={actionLoading === 'checkout'}
          />

          {/* Late check-in time confirmation modal */}
          <LateTimeModal
            open={lateCheckinPending}
            title="Late Check-in Detected"
            description="The driver arrived more than 10 minutes past their scheduled start time. Please confirm the exact arrival time below to accurately calculate detention."
            defaultTime={new Date().toISOString()}
            warehouseTimeZone={data?.warehouseTimeZone}
            onConfirm={(val) => {
              setLateCheckinPending(false);
              let overrideTime;
              if (val.includes('T') && !val.endsWith('Z')) {
                  const [d, t] = val.split('T');
                  overrideTime = { actualDate: d, actualTime: t };
              } else {
                  overrideTime = { checkInTime: val };
              }
              runAction('checkin', overrideTime);
            }}
            onCancel={() => setLateCheckinPending(false)}
            isSubmitting={actionLoading === 'checkin'}
          />

            {/* Accidental Completion Correction Dialog */}
            <AlertDialog open={showCorrectionDialog} onOpenChange={setShowCorrectionDialog}>
              <AlertDialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 max-w-md">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-amber-600 dark:text-amber-400 flex items-center gap-2">
                    <RotateCcw className="w-5 h-5" /> Correct Accidental Completion?
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-slate-600 dark:text-slate-400 space-y-2 text-sm text-left">
                    <p>This will return the appointment to Arrived / Checked-In status.</p>
                    <p>The physical dock will not be reclaimed automatically.</p>
                    <p>Linked POs and Loads will not be reopened automatically.</p>
                    <p>You may need to correct linked records separately.</p>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="my-2">
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1 block">Reason for correction <span className="text-red-500">*</span></label>
                  <textarea 
                    value={correctionReason}
                    onChange={(e) => setCorrectionReason(e.target.value)}
                    placeholder="e.g., Completed by mistake"
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 outline-none min-h-[80px]"
                    maxLength={1000}
                  />
                  <div className="text-right text-[10px] text-slate-400 mt-1">{correctionReason.length}/1000</div>
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel className="text-slate-700 dark:text-slate-300" disabled={correctionLoading} onClick={() => {
                    setCorrectionReason('');
                    setShowCorrectionDialog(false);
                  }}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50"
                    disabled={correctionLoading || !correctionReason.trim()}
                    onClick={async (e) => {
                      e.preventDefault();
                      if (!correctionReason.trim() || !token || !appointmentId) return;
                      setCorrectionLoading(true);
                      try {
                        const res = await fetch(`/api/appointments/${appointmentId}/correct-completion`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ reason: correctionReason.trim() })
                        });
                        if (!res.ok) {
                          const err = await res.json().catch(() => ({}));
                          throw new Error(err.error || 'Correction failed');
                        }
                        const payload = await res.json();
                        toast.success('Appointment returned to Checked-In.');
                        setCorrectionReason('');
                        setShowCorrectionDialog(false);
                        if (payload.warnings && payload.warnings.length > 0) {
                          setCorrectionWarnings(payload.warnings);
                        }
                        await mutate();
                        notifyActionSuccess();
                      } catch (err: any) {
                        toast.error(err.message || 'Failed to correct completion');
                      } finally {
                        setCorrectionLoading(false);
                      }
                    }}
                  >
                    {correctionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Correct Completion
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {/* Post-Correction Warnings Dialog */}
            <AlertDialog open={correctionWarnings.length > 0} onOpenChange={(open) => !open && setCorrectionWarnings([])}>
              <AlertDialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 max-w-md">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-amber-600 dark:text-amber-400 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" /> Review Downstream Impacts
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-slate-600 dark:text-slate-400 text-left">
                    <p className="mb-3">The appointment was corrected successfully, but you must review these linked records:</p>
                    <ul className="space-y-2 list-disc pl-5">
                      {correctionWarnings.map((w, i) => (
                        <li key={i} className="text-sm font-medium">{w.message}</li>
                      ))}
                    </ul>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogAction onClick={() => setCorrectionWarnings([])} className="bg-blue-600 hover:bg-blue-500 text-white">
                    Understood
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

        </DialogContent>
      </DialogPortal>
    </Dialog >

    {/* Confirmation AlertDialog  === single instance driven by pendingConfirm state */}
    <AlertDialog open={!!pendingConfirm} onOpenChange={(open) => { if (!open) setPendingConfirm(null); }}>
      <AlertDialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-slate-900 dark:text-slate-100">{pendingConfirm?.title}</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-600 dark:text-slate-400">{pendingConfirm?.description}</AlertDialogDescription>
        </AlertDialogHeader>
        {pendingConfirm?.action === 'approve' && (
          <div className="px-1 py-2">
            <label htmlFor="approve-dock-select" className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-1.5"><span>Assign Dock</span><span className="text-[11px] font-medium normal-case text-emerald-600 dark:text-emerald-400">{nonConflictingDocks.length} of {availableDocks.length} docks open</span></label>
            <select
              id="approve-dock-select"
              aria-label="Assign Dock"
              value={reassignDockId || pendingConfirm?.body?.dockId || preferredDefaultDockId || ''}
              onChange={(e) => setReassignDockId(e.target.value)}
              className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-md px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">Select dock...</option>
              {availableDocks.map((d: any) => {
                const capLabel = formatDockCapabilities(d);
                const isConflicting = conflictingDockIds.has(d.id);
                const isRecommended = !isConflicting && d.id === preferredDefaultDockId;
                return (
                  <option key={d.id} value={d.id} disabled={isConflicting}>
                    Dock {d.dockNumber}{capLabel ? ` — ${capLabel}` : ''}{d.type ? ` (${d.type})` : ''}{isRecommended ? ' (Recommended)' : ''}{isConflicting ? ' — Occupied (overlapping slot)' : ''}
                  </option>
                );
              })}
            </select>
          </div>
        )}
        {pendingConfirm?.content && (
          <div className="px-1">{pendingConfirm.content}</div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel className="text-slate-700 dark:text-slate-300">Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={pendingConfirm?.confirmClass || 'bg-blue-600 text-white'}
            onClick={() => {
              if (pendingConfirm) {
                const effectiveBody =
                  pendingConfirm.action === 'approve'
                    ? {
                        ...(pendingConfirm.body || {}),
                        dockId:
                          reassignDockId ||
                          pendingConfirm.body?.dockId ||
                          preferredDefaultDockId ||
                          '',
                      }
                    : pendingConfirm.body;
                runAction(pendingConfirm.action, effectiveBody);
              }
              setPendingConfirm(null);
            }}
          >
            {pendingConfirm?.confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Unresolved shortage warning  === fires before completion when shortages exist without follow-up */}
    <AlertDialog open={!!shortageWarningPending} onOpenChange={(open) => { if (!open) setShortageWarningPending(null); }}>
      <AlertDialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-red-600 dark:text-red-400 flex items-center gap-2">
            <XCircle className="w-5 h-5" /> Unresolved shortage detected
          </AlertDialogTitle>
          <AlertDialogDescription className="text-slate-600 dark:text-slate-400">
            One or more POs were marked short, but no follow-up was created.{' '}
            Closing out now will close all linked POs and no follow-up will be generated.{' '}
            Are you sure you want to continue?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="text-slate-700 dark:text-slate-300">Go Back</AlertDialogCancel>
          <AlertDialogAction
            className="bg-red-600 hover:bg-red-500 text-white"
            onClick={() => {
              if (shortageWarningPending?.confirmPayload) {
                setPendingConfirm(shortageWarningPending.confirmPayload);
              }
              setShortageWarningPending(null);
            }}
          >
            Close Out Anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Receipt gate  === blocks completion when unloading results are missing */}
    <AlertDialog open={receiptGateOpen} onOpenChange={(open) => { if (!open) setReceiptGateOpen(false); }}>
      <AlertDialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-amber-600 dark:text-amber-400 flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5" /> Unloading results required
          </AlertDialogTitle>
          <AlertDialogDescription className="text-slate-600 dark:text-slate-400">
            All linked POs must have unloading results recorded before this appointment can be completed. Please enter receiving data for each PO first.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="text-slate-700 dark:text-slate-300">Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-cyan-600 hover:bg-cyan-500 text-white"
            onClick={() => {
              setReceiptGateOpen(false);
              startReceiptEditing('new');
            }}
          >
            Review Unloading Results
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

// Separate component for Outcome Section to keep main file cleaner (or inline if prefered)
// I will inline the logic in the main Overview tab section for now, but I need to import CreateFollowUpDialog at the top.








